import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate as formatDateUtil } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { 
  DollarSign, 
  Plus, 
  Edit, 
  XCircle, 
  Loader2, 
  AlertTriangle, 
  Lock,
  FileText,
  Search,
  Filter,
  TrendingUp,
  ChevronDown,
  ChevronUp,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ParticipantCostsPanel = ({ caseId, caseData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [billingItems, setBillingItems] = useState([]);
  const [enrollmentContractId, setEnrollmentContractId] = useState(null);
  const [enrollmentContractName, setEnrollmentContractName] = useState('');
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showVoidDialog, setShowVoidDialog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Expanded categories in summary
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Form state
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split('T')[0],
    category_id: '',
    category_name: '',
    billing_item_id: '',
    billing_item_name: '',
    description: '',
    quantity: 1,
    unit_cost: 0,
    total_amount: 0,
    reference_number: '',
    status: 'draft'
  });
  
  const [voidReason, setVoidReason] = useState('');

  // Roles that can access participant costs
  const ALLOWED_ROLES = ['system_admin', 'executive', 'operations_manager'];
  const hasAccess = ALLOWED_ROLES.includes(user?.role);

  const fetchAccessData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}/participant-costs/check-access`);
      setAccessData(response.data);
    } catch (error) {
      console.error('Failed to check access:', error);
      setAccessData({ has_access: false, show_tab: false });
    }
  }, [caseId]);

  const fetchTransactions = useCallback(async () => {
    if (!hasAccess) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cases/${caseId}/participant-costs`);
      setTransactions(response.data.transactions || []);
      setCategories(response.data.categories || []);
      setBillingItems(response.data.billing_items || []);
      setEnrollmentContractId(response.data.enrollment_contract_id || null);
      setEnrollmentContractName(response.data.enrollment_contract_name || '');
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || null);
    } catch (error) {
      console.error('Failed to fetch participant costs:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load participant costs');
      }
    } finally {
      setLoading(false);
    }
  }, [caseId, hasAccess]);

  useEffect(() => {
    fetchAccessData();
    if (hasAccess) {
      fetchTransactions();
    } else {
      setLoading(false);
    }
  }, [fetchAccessData, fetchTransactions, hasAccess]);

  const handleCategoryChange = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      category_name: category?.name || '',
      billing_item_id: '',
      billing_item_name: '',
      unit_cost: 0,
      total_amount: prev.quantity * 0
    }));
  };

  const handleBillingItemChange = (itemId) => {
    const item = billingItems.find(i => i.id === itemId);
    const unitCost = item?.default_cost || 0;
    setFormData(prev => ({
      ...prev,
      billing_item_id: itemId,
      billing_item_name: item?.name || '',
      unit_cost: unitCost,
      total_amount: prev.quantity * unitCost
    }));
  };

  const handleQuantityChange = (quantity) => {
    const qty = parseFloat(quantity) || 0;
    setFormData(prev => ({
      ...prev,
      quantity: qty,
      total_amount: qty * prev.unit_cost
    }));
  };

  const handleUnitCostChange = (cost) => {
    const unitCost = parseFloat(cost) || 0;
    setFormData(prev => ({
      ...prev,
      unit_cost: unitCost,
      total_amount: prev.quantity * unitCost
    }));
  };

  const resetForm = () => {
    setFormData({
      transaction_date: new Date().toISOString().split('T')[0],
      category_id: '',
      category_name: '',
      billing_item_id: '',
      billing_item_name: '',
      description: '',
      quantity: 1,
      unit_cost: 0,
      total_amount: 0,
      reference_number: '',
      status: 'draft'
    });
    setSelectedTransaction(null);
  };

  const handleSubmit = async () => {
    if (!formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.billing_item_id) {
      toast.error('Please select a billing item');
      return;
    }
    if (!formData.transaction_date) {
      toast.error('Please select a transaction date');
      return;
    }

    setSaving(true);
    try {
      if (selectedTransaction) {
        // Update existing
        await axios.put(`${API}/cases/${caseId}/participant-costs/${selectedTransaction.id}`, formData);
        toast.success('Transaction updated successfully');
      } else {
        // Create new
        await axios.post(`${API}/cases/${caseId}/participant-costs`, formData);
        toast.success('Transaction created successfully');
      }
      setShowAddDialog(false);
      resetForm();
      fetchTransactions();
    } catch (error) {
      console.error('Failed to save transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to save transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (transaction) => {
    setSelectedTransaction(transaction);
    setFormData({
      transaction_date: transaction.transaction_date,
      category_id: transaction.category_id,
      category_name: transaction.category_name,
      billing_item_id: transaction.billing_item_id,
      billing_item_name: transaction.billing_item_name,
      description: transaction.description || '',
      quantity: transaction.quantity || 1,
      unit_cost: transaction.unit_cost || 0,
      total_amount: transaction.total_amount || 0,
      reference_number: transaction.reference_number || '',
      status: transaction.status
    });
    setShowAddDialog(true);
  };

  const handleVoid = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason for voiding this transaction');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/cases/${caseId}/participant-costs/${selectedTransaction.id}/void`, {
        void_reason: voidReason
      });
      toast.success('Transaction voided successfully');
      setShowVoidDialog(false);
      setSelectedTransaction(null);
      setVoidReason('');
      fetchTransactions();
    } catch (error) {
      console.error('Failed to void transaction:', error);
      toast.error(error.response?.data?.detail || 'Failed to void transaction');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategoryExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await axios.put(`${API}/cases/${caseId}/participant-costs/alerts/${alertId}/dismiss`);
      toast.success('Alert dismissed');
      fetchTransactions();
    } catch (error) {
      console.error('Failed to dismiss alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'posted':
        return <Badge className="bg-green-100 text-green-800">Posted</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      case 'voided':
        return <Badge variant="destructive">Voided</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (statusFilter !== 'all' && t.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && t.category_id !== categoryFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        t.billing_item_name?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search) ||
        t.contract_name?.toLowerCase().includes(search) ||
        t.reference_number?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Get billing items for selected category
  const categoryBillingItems = billingItems.filter(
    item => item.category_id === formData.category_id && item.is_active
  );

  // Access denied view
  if (!hasAccess) {
    return (
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12">
            <Lock className="w-16 h-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">Access Denied</h3>
            <p className="text-muted-foreground text-center max-w-md">
              You do not have permission to view Participant Cost information.
              This feature is restricted to System Administrators, Executives, and Operations Managers.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="card-shadow">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            <span className="ml-3 text-muted-foreground">Loading participant costs...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cost Summary Section */}
      {summary && (
        <Card className="card-shadow border-purple-200 dark:border-purple-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Cost Summary
            </CardTitle>
            <CardDescription>
              Total costs for this SNAP E&T case (Posted transactions only)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Grand Total */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <div className="w-14 h-14 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <DollarSign className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Participant Costs</p>
                <p className="text-3xl font-bold text-purple-600">{formatCurrency(summary.grand_total)}</p>
                <p className="text-xs text-muted-foreground">
                  {summary.posted_count} posted transaction(s) of {summary.transaction_count} total
                </p>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">By Category</h4>
              {categories.map(cat => {
                const catTotal = summary.category_totals[cat.id] || 0;
                const isExpanded = expandedCategories[cat.id];
                const catItems = billingItems.filter(item => item.category_id === cat.id);
                
                return (
                  <div key={cat.id} className="border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between p-3 bg-muted/30 hover:bg-muted/50 transition-colors"
                      onClick={() => toggleCategoryExpanded(cat.id)}
                    >
                      <span className="font-medium">{cat.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-purple-600">{formatCurrency(catTotal)}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </button>
                    
                    {isExpanded && (
                      <div className="p-3 border-t bg-background">
                        {catItems.length > 0 ? (
                          <div className="space-y-2">
                            {catItems.map(item => {
                              const itemTotal = summary.billing_item_totals[item.id] || 0;
                              return (
                                <div key={item.id} className="flex justify-between text-sm pl-4">
                                  <span className="text-muted-foreground">{item.name}</span>
                                  <span>{formatCurrency(itemTotal)}</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground text-center py-2">No billing items in this category</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <Alert variant="destructive" className="border-orange-500 bg-orange-50" data-testid="participant-cost-alerts">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertTitle className="text-orange-800">Action Required</AlertTitle>
          <AlertDescription className="text-orange-700">
            {alerts.map(alert => (
              <div key={alert.id} className="mt-2 flex items-start justify-between gap-4">
                <p>{alert.message}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => handleDismissAlert(alert.id)}
                  className="shrink-0 border-orange-300 text-orange-700 hover:bg-orange-100"
                  data-testid={`dismiss-alert-${alert.id}`}
                >
                  Dismiss
                </Button>
              </div>
            ))}
          </AlertDescription>
        </Alert>
      )}

      {/* Transaction Ledger */}
      <Card className="card-shadow">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Transaction Ledger
              </CardTitle>
              <CardDescription>
                All participant cost transactions for this case
              </CardDescription>
            </div>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-transaction-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Transaction
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-transactions"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="posted">Posted</SelectItem>
                <SelectItem value="voided">Voided</SelectItem>
              </SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Transactions Table */}
          {filteredTransactions.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Billing Item</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTransactions.map(transaction => (
                    <TableRow key={transaction.id} className={transaction.status === 'voided' ? 'opacity-50' : ''}>
                      <TableCell>{formatDateUtil(transaction.transaction_date)}</TableCell>
                      <TableCell>{transaction.category_name}</TableCell>
                      <TableCell>{transaction.billing_item_name}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{transaction.description || '-'}</TableCell>
                      <TableCell className="text-right">{transaction.quantity}</TableCell>
                      <TableCell className="text-right">{formatCurrency(transaction.unit_cost)}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(transaction.total_amount)}</TableCell>
                      <TableCell>{transaction.contract_name || '-'}</TableCell>
                      <TableCell>{transaction.reference_number || '-'}</TableCell>
                      <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                      <TableCell className="text-right">
                        {transaction.status !== 'voided' && (
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(transaction)}
                              data-testid={`edit-transaction-${transaction.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedTransaction(transaction);
                                setShowVoidDialog(true);
                              }}
                              data-testid={`void-transaction-${transaction.id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No transactions found</p>
              <p className="text-sm mt-1">Click "Add Transaction" to create the first cost entry</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => { if (!open) resetForm(); setShowAddDialog(open); }}>
        <DialogContent className="max-w-lg" data-testid="transaction-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedTransaction ? 'Edit Transaction' : 'Add Transaction'}
            </DialogTitle>
            <DialogDescription>
              {selectedTransaction ? 'Update the transaction details below' : 'Enter the cost details for this participant'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Transaction Date *</Label>
                <Input
                  type="date"
                  value={formData.transaction_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, transaction_date: e.target.value }))}
                  data-testid="transaction-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger data-testid="transaction-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="posted">Posted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={formData.category_id} onValueChange={handleCategoryChange}>
                <SelectTrigger data-testid="transaction-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.is_active).map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Billing Item *</Label>
              <Select 
                value={formData.billing_item_id} 
                onValueChange={handleBillingItemChange}
                disabled={!formData.category_id}
              >
                <SelectTrigger data-testid="transaction-billing-item">
                  <SelectValue placeholder={formData.category_id ? "Select billing item" : "Select category first"} />
                </SelectTrigger>
                <SelectContent>
                  {categoryBillingItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} {item.default_cost > 0 && `(Default: ${formatCurrency(item.default_cost)})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={formData.quantity}
                  onChange={(e) => handleQuantityChange(e.target.value)}
                  data-testid="transaction-quantity"
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Cost</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.unit_cost}
                  onChange={(e) => handleUnitCostChange(e.target.value)}
                  data-testid="transaction-unit-cost"
                />
              </div>
              <div className="space-y-2">
                <Label>Total Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.total_amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_amount: parseFloat(e.target.value) || 0 }))}
                  data-testid="transaction-total"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contract (from Enrollment)</Label>
                <Input
                  value={enrollmentContractName || 'No contract assigned'}
                  disabled
                  className="bg-muted"
                  data-testid="transaction-contract-readonly"
                />
                <p className="text-xs text-muted-foreground">Auto-populated from enrollment</p>
              </div>
              <div className="space-y-2">
                <Label>Reference/Invoice #</Label>
                <Input
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="e.g., INV-12345"
                  data-testid="transaction-reference"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description/Memo</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Additional details about this transaction..."
                rows={2}
                data-testid="transaction-description"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetForm(); setShowAddDialog(false); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="save-transaction-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {selectedTransaction ? 'Update Transaction' : 'Create Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void Transaction Dialog */}
      <Dialog open={showVoidDialog} onOpenChange={setShowVoidDialog}>
        <DialogContent data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Void Transaction
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. The transaction will be marked as voided and excluded from totals.
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="py-4">
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Transaction Details</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 text-sm">
                    <p><strong>Date:</strong> {formatDateUtil(selectedTransaction.transaction_date)}</p>
                    <p><strong>Item:</strong> {selectedTransaction.billing_item_name}</p>
                    <p><strong>Amount:</strong> {formatCurrency(selectedTransaction.total_amount)}</p>
                  </div>
                </AlertDescription>
              </Alert>
              
              <div className="space-y-2">
                <Label>Reason for Voiding *</Label>
                <Textarea
                  value={voidReason}
                  onChange={(e) => setVoidReason(e.target.value)}
                  placeholder="Please provide a reason for voiding this transaction..."
                  rows={3}
                  data-testid="void-reason"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowVoidDialog(false); setVoidReason(''); }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleVoid} disabled={saving || !voidReason.trim()} data-testid="confirm-void-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Void Transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParticipantCostsPanel;
